import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { type UseFormReturn, useFieldArray, useForm } from "react-hook-form"

import { MAX_SURCHARGES_PER_ROW } from "@/screens/Pricing/components/tables/constants"
import type { PricingGridRow } from "@/screens/Pricing/components/tables/types"
import { type SurchargeFormValues, surchargeFormSchema } from "@/screens/Pricing/schema/SurchargeFormSchema"
import { toSurchargeFormValues, toSurchargeRowUpdate } from "@/screens/Pricing/utils/surcharge"

interface UseSurchargeFormProps {
  row: PricingGridRow
  onSubmit: (update: ReturnType<typeof toSurchargeRowUpdate>) => void
  onClose: () => void
}

interface UseSurchargeFormReturn {
  methods: UseFormReturn<SurchargeFormValues>
  fields: ReturnType<typeof useFieldArray<SurchargeFormValues, "surcharges">>["fields"]
  handleAppend: () => void
  handleRemove: (index: number) => void
  handleSubmit: () => void
  handleCancel: () => void
  canSubmit: boolean
  canAppend: boolean
  canRemove: boolean
  isCancelConfirmationOpen: boolean
  setIsCancelConfirmationOpen: (value: boolean) => void
}

export const useSurchargeForm = ({ row, onSubmit, onClose }: UseSurchargeFormProps): UseSurchargeFormReturn => {
  const methods = useForm<SurchargeFormValues>({
    defaultValues: toSurchargeFormValues(row),
    resolver: zodResolver(surchargeFormSchema),
    mode: "onChange",
  })

  const {
    control,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const { fields, append, remove } = useFieldArray({ control, name: "surcharges" })

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const canAppend = fields.length < MAX_SURCHARGES_PER_ROW
  const canRemove = fields.length > 1

  const handleAppend = () => {
    if (!canAppend) return
    append({ price: null, description: "" })
  }

  const handleRemove = (index: number) => {
    if (!canRemove) return
    remove(index)
  }

  const handleSubmit = methods.handleSubmit((values) => {
    onSubmit(toSurchargeRowUpdate(values))
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
      return
    }
    onClose()
  }

  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  return {
    methods,
    fields,
    handleAppend,
    handleRemove,
    handleSubmit,
    handleCancel,
    canSubmit,
    canAppend,
    canRemove,
    isCancelConfirmationOpen,
    setIsCancelConfirmationOpen,
  }
}
